/**
 * Global TanStack Query Key Store
 *
 * Centralized query key management for all hand-written and auto-generated queries.
 * Provides type-safe key creation, invalidation, and refetch utilities.
 *
 * Inspired by @lukemorales/query-key-factory
 */

import type { InvalidateQueryFilters, QueryClient } from '@tanstack/react-query';

import { useSearchImagesInfiniteQuery } from './queries/search-images/search-images';
import type { SearchImagesInfiniteQueryVariables } from './queries/search-images/search-images';

import { useGetPdpQuery } from './queries/get-pdp/get-pdp';
import type { GetPdpQueryVariables } from './queries/get-pdp/get-pdp';
import { useSearchBenchmarksQuery } from './queries/search-benchmarks/search-benchmarks';

import { useGetImageOrderQuery } from './queries/get-image-order/get-image-order';
import type { GetImageOrderQueryVariables } from './queries/get-image-order/get-image-order';

import { useGetImageCountQuery } from './queries/get-image-count/get-image-count';
import type { GetImageCountQueryVariables } from './queries/get-image-count/get-image-count';

import { useGetImageMixQuery } from './queries/get-image-mix/get-image-mix';
import type { GetImageMixQueryVariables } from './queries/get-image-mix/get-image-mix';

import { useGetImageScoresQuery } from './queries/get-image-scores/get-image-scores';
import type { GetImageScoresQueryVariables } from './queries/get-image-scores/get-image-scores';

import { useGetRecommendationsQuery } from './queries/get-recommendations/get-recommendations';
import type { GetRecommendationsQueryVariables } from './queries/get-recommendations/get-recommendations';

import { useGetImageQuery } from './queries/get-image/get-image';
import type { GetImageQueryVariables } from './queries/get-image/get-image';

import type { SearchPdpImagesInfiniteQueryVariables } from './queries/search-pdp-images/search-pdp-images';
import type { SearchPdpImagesQueryVariables } from './queries/search-pdp-images/search-pdp-images';

import type { SearchPdpsInfiniteQueryVariables } from './mutations/complex-search-pdps/types';

import type { SearchCollectionsInfiniteQueryVariables } from './queries/search-collections/search-collections';

import { useGetCollectionQuery } from './queries/get-collection/get-collection';
import type { GetCollectionQueryVariables } from './queries/get-collection/get-collection';

import type { SearchCollectionImagesInfiniteQueryVariables } from './queries/search-collection-images/search-collection-images';

import { useGetUsersForOrganizationQuery } from './queries/get-users-for-organization/get-users-for-organization';
import type { GetUsersForOrganizationQueryVariables } from './queries/get-users-for-organization/get-users-for-organization';

import { useGetOrganizationsForUserQuery } from './queries/get-organizations-for-user/get-organizations-for-user';
import type { GetOrganizationsForUserQueryVariables } from './queries/get-organizations-for-user/get-organizations-for-user';

import { useGetOrganizationQuery } from './queries/get-organization/get-organization';
import type { GetOrganizationQueryVariables } from './queries/get-organization/get-organization';

import { useGetMeContextQuery } from './queries/get-me-context/get-me-context';

import { useSearchRoleAssignmentsQuery } from './queries/search-role-assignments/search-role-assignments';
import type { SearchRoleAssignmentsQueryVariables } from './queries/search-role-assignments/search-role-assignments';

import { useGetIcpProductCategoryQuery } from './queries/get-icp-product-category/get-icp-product-category';
import type { GetIcpProductCategoryQueryVariables } from './queries/get-icp-product-category/get-icp-product-category';

import { useGetProductCategoryQuery } from './queries/get-product-category/get-product-category';
import type { GetProductCategoryQueryVariables } from './queries/get-product-category/get-product-category';

import { useGetImageClassificationQuery } from './queries/get-image-classification/get-image-classification';
import type { GetImageClassificationQueryVariables } from './queries/get-image-classification/get-image-classification';

/**
 * Global query key store
 *
 * Organized by domain (images, products, recommendations) with nested structure.
 * Each domain supports:
 * - list(variables) - for regular queries
 * - infinite(variables) - for infinite queries (where applicable)
 * - detail(variables) - for single-item queries
 */
export const queryKeys = {
  /**
   * Image-related queries
   */
  images: {
    /**
     * Search images infinite query
     * Wraps useSearchImagesInfiniteQuery.getKey()
     */
    infinite: (variables: SearchImagesInfiniteQueryVariables) => ({
      queryKey: useSearchImagesInfiniteQuery.getKey(variables),
    }),
    /**
     * Get single image
     * Wraps useGetImageQuery.getKey()
     */
    detail: (variables: GetImageQueryVariables) => ({
      queryKey: useGetImageQuery.getKey(variables),
    }),
  },

  /**
   * Product/PDP-related queries
   */
  products: {
    /**
     * Search/list PDPs (infinite scroll)
     * Hand-written query from @vizit/products package
     * Key pattern: ['SearchPdpsPaginated', variables]
     */
    infinite: (variables: SearchPdpsInfiniteQueryVariables) => ({
      queryKey: ['SearchPdpsPaginated', variables] as const,
    }),
    /**
     * Get single PDP
     * Wraps useGetPdpQuery.getKey()
     */
    detail: (variables: GetPdpQueryVariables) => ({
      queryKey: useGetPdpQuery.getKey(variables),
    }),
    /**
     * Search PDP images (infinite scroll)
     * Wraps useSearchPdpImagesInfiniteQuery.getKey()
     */
    pdpImages: (variables: SearchPdpImagesInfiniteQueryVariables) => ({
      queryKey: ['SearchPdpImagesInfinite', variables] as const,
    }),
    /**
     * Search PDP images (single-page, non-infinite)
     * Matches useSearchPdpImagesQuery keys. Accepts partial variables so a
     * whole family can be targeted for invalidation (TanStack partial
     * matching); call with no argument to target every such query.
     */
    pdpImagesSearch: (variables?: Partial<SearchPdpImagesQueryVariables>) => ({
      queryKey: variables ? (['SearchPdpImages', variables] as const) : (['SearchPdpImages'] as const),
    }),
  },

  /**
   * Collection-related queries
   */
  collections: {
    /**
     * Search collections infinite query
     * Note: useSearchCollectionsInfiniteQuery doesn't have .getKey(), so we create it manually
     */
    infinite: (variables: SearchCollectionsInfiniteQueryVariables) => ({
      queryKey: ['SearchCollectionsInfinite', variables] as const,
    }),
    /**
     * Get single collection
     * Wraps useGetCollectionQuery.getKey()
     */
    detail: (variables: GetCollectionQueryVariables) => ({
      queryKey: useGetCollectionQuery.getKey(variables),
    }),
    /**
     * Search collection images (infinite scroll)
     * Key pattern: ['SearchCollectionImagesInfinite', variables]
     */
    collectionImages: (variables: SearchCollectionImagesInfiniteQueryVariables) => ({
      queryKey: ['SearchCollectionImagesInfinite', variables] as const,
    }),
  },

  /**
   * Organization-related queries
   */
  organizations: {
    /**
     * Get single organization
     * Wraps useGetOrganizationQuery.getKey()
     */
    detail: (variables: GetOrganizationQueryVariables) => ({
      queryKey: useGetOrganizationQuery.getKey(variables),
    }),
    /**
     * Get organizations for a user
     * Wraps useGetOrganizationsForUserQuery.getKey()
     */
    forUser: (variables: GetOrganizationsForUserQueryVariables) => ({
      queryKey: useGetOrganizationsForUserQuery.getKey(variables),
    }),
    /**
     * Get users for an organization
     * Wraps useGetUsersForOrganizationQuery.getKey()
     */
    users: (variables: GetUsersForOrganizationQueryVariables) => ({
      queryKey: useGetUsersForOrganizationQuery.getKey(variables),
    }),
  },

  /**
   * Access / me-context queries
   */
  access: {
    /**
     * Current user access context (`GET /v1/me`)
     * Wraps useGetMeContextQuery.getKey()
     */
    meContext: () => ({
      queryKey: useGetMeContextQuery.getKey(),
    }),
    /**
     * Role assignments for an organization (`GET /v1/role-assignments`)
     * Wraps useSearchRoleAssignmentsQuery.getKey()
     */
    roleAssignments: (variables: SearchRoleAssignmentsQueryVariables) => ({
      queryKey: useSearchRoleAssignmentsQuery.getKey(variables),
    }),
  },

  /**
   * Product recommendation/penalty queries
   */
  recommendations: {
    /**
     * Image order penalty query
     * Wraps useGetImageOrderQuery.getKey()
     */
    imageOrder: (variables: GetImageOrderQueryVariables) => ({
      queryKey: useGetImageOrderQuery.getKey(variables),
    }),
    /**
     * Image count penalty query
     * Wraps useGetImageCountQuery.getKey()
     */
    imageCount: (variables: GetImageCountQueryVariables) => ({
      queryKey: useGetImageCountQuery.getKey(variables),
    }),
    /**
     * Image mix penalty query
     * Wraps useGetImageMixQuery.getKey()
     */
    imageMix: (variables: GetImageMixQueryVariables) => ({
      queryKey: useGetImageMixQuery.getKey(variables),
    }),
    /**
     * Image scores penalty query
     * Wraps useGetImageScoresQuery.getKey()
     */
    imageScores: (variables: GetImageScoresQueryVariables) => ({
      queryKey: useGetImageScoresQuery.getKey(variables),
    }),
    /**
     * Recommendations V2 query (all metrics in one endpoint)
     * Wraps useGetRecommendationsQuery.getKey()
     */
    v2: (variables: GetRecommendationsQueryVariables) => ({
      queryKey: useGetRecommendationsQuery.getKey(variables),
    }),
  },

  /**
   * Category and classification queries
   */
  categories: {
    /**
     * Get ICP product category
     * Wraps useGetIcpProductCategoryQuery.getKey()
     */
    icpProduct: (variables: GetIcpProductCategoryQueryVariables) => ({
      queryKey: useGetIcpProductCategoryQuery.getKey(variables),
    }),
    /**
     * Get product category
     * Wraps useGetProductCategoryQuery.getKey()
     */
    product: (variables: GetProductCategoryQueryVariables) => ({
      queryKey: useGetProductCategoryQuery.getKey(variables),
    }),
    /**
     * Get image classification
     * Wraps useGetImageClassificationQuery.getKey()
     */
    imageClassification: (variables: GetImageClassificationQueryVariables) => ({
      queryKey: useGetImageClassificationQuery.getKey(variables),
    }),
  },
} as const;

/**
 * Utility helpers for common query operations
 */
export const queryUtils = {
  /**
   * Invalidate all product-related queries
   * Matches: SearchPdps*, GetPdp*
   */
  invalidateAllProducts: (queryClient: QueryClient) => {
    return queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey[0];
        return typeof key === 'string' && (key.startsWith('SearchPdps') || key.startsWith('GetPdp'));
      },
    });
  },

  /**
   * Invalidate all PDP list queries (both regular and infinite scroll)
   * Matches: SearchPdps, SearchPdpsPaginated
   * @param queryClient - The React Query client
   * @param workspaceId - Optional portfolio ID to scope invalidation to specific portfolio
   */
  invalidateAllPdpLists: (queryClient: QueryClient, workspaceId?: string) => {
    return queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey[0];
        if (typeof key !== 'string' || !key.startsWith('SearchPdps')) {
          return false;
        }
        // If workspaceId is provided, only invalidate queries for that portfolio
        if (workspaceId && query.queryKey[1]) {
          const variables = query.queryKey[1] as Record<string, unknown>;
          return variables.workspace_id === workspaceId;
        }
        return true;
      },
    });
  },

  /**
   * Invalidate organizations for a specific user
   */
  invalidateOrganizationsForUser: (queryClient: QueryClient, userId: string) => {
    return queryClient.invalidateQueries({
      queryKey: queryKeys.organizations.forUser({ user_id: userId }).queryKey,
    });
  },

  /**
   * Invalidate users for a specific organization
   */
  invalidateUsersForOrganization: (queryClient: QueryClient, organizationId: string) => {
    return queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        if (Array.isArray(key) && key.length >= 2 && typeof key[1] === 'object' && key[1] !== null) {
          const vars = key[1] as Record<string, unknown>;
          return key[0] === 'GetUsersForOrganization' && vars.organization_id === organizationId;
        }
        return false;
      },
    });
  },

  /**
   * Invalidate role assignments for a specific organization
   */
  invalidateRoleAssignments: (queryClient: QueryClient, organizationId: string) => {
    return queryClient.invalidateQueries({
      queryKey: queryKeys.access.roleAssignments({ organization_id: organizationId }).queryKey,
    });
  },

  /**
   * Invalidate all recommendation queries for a specific PDP
   */
  invalidateAllRecommendations: (queryClient: QueryClient, workspaceId: string, pdpId: string) => {
    return Promise.all([
      queryClient.invalidateQueries({
        queryKey: queryKeys.recommendations.imageOrder({ workspace_id: workspaceId, pdp_id: pdpId }).queryKey,
      }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.recommendations.imageCount({ workspace_id: workspaceId, pdp_id: pdpId }).queryKey,
      }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.recommendations.imageMix({ workspace_id: workspaceId, pdp_id: pdpId }).queryKey,
      }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.recommendations.imageScores({ workspace_id: workspaceId, pdp_id: pdpId }).queryKey,
      }),
    ]);
  },

  /**
   * Refetch all recommendations for a specific PDP
   */
  refetchAllRecommendations: (queryClient: QueryClient, workspaceId: string, pdpId: string) => {
    return Promise.all([
      queryClient.refetchQueries({
        queryKey: queryKeys.recommendations.imageOrder({ workspace_id: workspaceId, pdp_id: pdpId }).queryKey,
      }),
      queryClient.refetchQueries({
        queryKey: queryKeys.recommendations.imageCount({ workspace_id: workspaceId, pdp_id: pdpId }).queryKey,
      }),
      queryClient.refetchQueries({
        queryKey: queryKeys.recommendations.imageMix({ workspace_id: workspaceId, pdp_id: pdpId }).queryKey,
      }),
      queryClient.refetchQueries({
        queryKey: queryKeys.recommendations.imageScores({ workspace_id: workspaceId, pdp_id: pdpId }).queryKey,
      }),
    ]);
  },

  /**
   * Invalidate a specific product by ID regardless of organization/portfolio
   */
  invalidateProductById: (queryClient: QueryClient, pdpId: string) => {
    return queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        if (Array.isArray(key) && key.length >= 2 && typeof key[1] === 'object' && key[1] !== null) {
          const vars = key[1] as Record<string, unknown>;
          return key[0] === 'GetPdp' && vars.pdp_id === pdpId;
        }
        return false;
      },
    });
  },

  /**
   * Invalidate all collection list queries for a portfolio
   * Matches: SearchCollectionsInfinite with any pagination parameters, and the
   * single-page SearchCollections query (e.g. the Library's collection count)
   * @param queryClient - The React Query client
   * @param workspaceId - Portfolio ID to scope invalidation
   */
  invalidateAllCollectionLists: (queryClient: QueryClient, workspaceId: string) => {
    return queryClient.invalidateQueries({
      predicate: (query) => {
        const [name, variables] = query.queryKey;
        if (name === 'SearchCollectionsInfinite' || name === 'SearchCollections') {
          return (variables as Record<string, unknown> | undefined)?.workspace_id === workspaceId;
        }
        return false;
      },
    });
  },

  /**
   * Invalidate every benchmark list query of a workspace
   * Matches: SearchBenchmarks with any filter or sort parameters
   * @param queryClient - The React Query client
   * @param workspaceId - Workspace ID to scope invalidation
   * @param options - Pass `refetchType: 'none'` to only mark the lists stale, e.g. right before navigating away
   */
  invalidateAllBenchmarkLists: (
    queryClient: QueryClient,
    workspaceId: string,
    options?: Pick<InvalidateQueryFilters, 'refetchType'>,
  ) => {
    return queryClient.invalidateQueries({
      queryKey: useSearchBenchmarksQuery.getKey({ workspace_id: workspaceId }),
      ...options,
    });
  },

  /**
   * Invalidate a specific collection detail query
   * @param queryClient - The React Query client
   * @param workspaceId - Portfolio ID
   * @param collectionId - Collection ID to invalidate
   */
  invalidateCollectionDetail: (queryClient: QueryClient, workspaceId: string, collectionId: string) => {
    return queryClient.invalidateQueries({
      queryKey: queryKeys.collections.detail({ workspace_id: workspaceId, collection_id: collectionId }).queryKey,
    });
  },
};
